import { 
  collection, 
  doc, 
  getDoc, 
  getDocs,
  query, 
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();

// League Service for Firestore operations
export const leagueService = {
  /**
   * Create a new league (via Cloud Function)
   * @param {string} userId - User's Firebase UID
   * @param {Object} leagueData - League data (name, description)
   * @returns {Promise<Object>} Created league with invite code
   */
  createLeague: async (userId, leagueData) => {
    try {
      const createLeagueFunction = httpsCallable(functions, 'createLeague');
      const result = await createLeagueFunction({
        name: leagueData.name,
        description: leagueData.description || '',
      });

      console.log('✅ League created:', result.data);
      return result.data;
    } catch (error) {
      console.error('❌ Error creating league:', error);
      throw error;
    }
  },

  /**
   * Join a league using invite code (via Cloud Function)
   * @param {string} userId - User's Firebase UID
   * @param {string} inviteCode - League invite code
   * @returns {Promise<Object>} Join result
   */
  joinLeague: async (userId, inviteCode) => {
    try {
      const joinLeagueFunction = httpsCallable(functions, 'joinLeague');
      const result = await joinLeagueFunction({
        inviteCode: inviteCode.toUpperCase(),
      });

      console.log('✅ Joined league:', result.data);
      return result.data;
    } catch (error) {
      console.error('❌ Error joining league:', error);
      throw error;
    }
  },

  /**
   * Get all leagues user is a member of
   * @param {string} userId - User's Firebase UID
   * @returns {Promise<Array>} Array of leagues
   */
  getLeagues: async (userId) => {
    try {
      const leaguesQuery = query(
        collection(db, 'leagues'),
        where('members', 'array-contains', userId)
      );

      const querySnapshot = await getDocs(leaguesQuery);
      const leagues = [];

      querySnapshot.forEach((doc) => {
        leagues.push({ id: doc.id, ...doc.data() });
      });

      console.log('✅ Retrieved leagues:', leagues.length);
      return leagues;
    } catch (error) {
      console.error('❌ Error getting leagues:', error);
      throw error;
    }
  },

  /**
   * Get league by ID
   * @param {string} leagueId - League document ID
   * @returns {Promise<Object|null>} League data or null
   */
  getLeague: async (leagueId) => {
    try {
      const leagueRef = doc(db, 'leagues', leagueId);
      const leagueDoc = await getDoc(leagueRef);

      if (!leagueDoc.exists()) {
        console.log('ℹ️ League not found:', leagueId);
        return null;
      }

      return { id: leagueDoc.id, ...leagueDoc.data() };
    } catch (error) {
      console.error('❌ Error getting league:', error);
      throw error;
    }
  },

  /**
   * Get league standings
   * @param {string} leagueId - League document ID
   * @returns {Promise<Array>} Array of standings (sorted by points)
   */
  getLeagueStandings: async (leagueId) => {
    try {
      const standingsQuery = query(
        collection(db, 'leagues', leagueId, 'standings'),
        orderBy('totalPoints', 'desc')
      );

      const querySnapshot = await getDocs(standingsQuery);
      const standings = [];

      querySnapshot.forEach((doc, index) => {
        standings.push({ 
          position: index + 1,
          id: doc.id, 
          ...doc.data() 
        });
      });

      console.log('✅ Retrieved standings:', standings.length);
      return standings;
    } catch (error) {
      console.error('❌ Error getting league standings:', error);
      throw error;
    }
  },

  /**
   * Get league members
   * @param {string} leagueId - League document ID
   * @returns {Promise<Array>} Array of members
   */
  getLeagueMembers: async (leagueId) => {
    try {
      const membersQuery = query(
        collection(db, 'leagues', leagueId, 'members'),
        orderBy('joinedAt', 'asc')
      );

      const querySnapshot = await getDocs(membersQuery);
      const members = [];

      querySnapshot.forEach((doc) => {
        members.push({ id: doc.id, ...doc.data() });
      });

      console.log('✅ Retrieved members:', members.length);
      return members;
    } catch (error) {
      console.error('❌ Error getting league members:', error);
      throw error;
    }
  },
};

export default leagueService;

